import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Category, UnitType } from '../../../services/categories/interfaces/categories.interface';
import { CategoryService } from '../../../services/categories/categories.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MenuProductService } from '../../../services/menu-product/menu-product.service';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';
import { Select2Module } from 'ng-select2-component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CategoryDialogComponent } from '../../../pages/categories-admin/category-dialog.component';
import { PortionsService } from '../../../services/portions/portions.service';
import { PortionDialogComponent } from '../../../pages/categories-admin/portion-dialog.component';
import { MenuProductOption } from '../../../models/menu-product-option.model';
import { ButtonComponent } from '../../../ui/atoms/button/button.component';
import { InputComponent } from '../../../ui/atoms/input/input.component';

interface Select2Option {
  value: string;
  label: string;
}

@Component({
  selector: 'app-add-menu-product-from',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, Select2Module, MatDialogModule, MatButtonModule, MatIconModule, ButtonComponent, InputComponent],
  templateUrl: './add-menu-product-from.component.html',
  styleUrl: './add-menu-product-from.component.css'
})

export class AddMenuProductFromComponent implements OnInit {
  categoriesOptions: Select2Option[] = []
  unitTypesOptions: Select2Option[] = [];
  categories: Category[] = [];
  selectedCategory: any = undefined
  selectedUnitType: any = undefined
  loading = true;
  options: MenuProductOption[] = [];

  productForm: FormGroup;
  currentCategory: Category | undefined;

  @Input() productInfo: any = undefined
  @Output() created = new EventEmitter<any>();
  @Output() canceled = new EventEmitter<void>();

  @ViewChild('template') template: TemplateRef<any> | undefined;
  skuExists = false;
  nombreExists = false;

  // Price history
  priceHistory: any[] = [];
  histLoading = false;
  histSaving = false;
  histSellPrice: number | null = null;
  histCostPrice: number | null = null;
  histDate: string = new Date().toISOString().substring(0, 10);

  constructor(private cdr: ChangeDetectorRef, private categoryService: CategoryService, private fb: FormBuilder, private menuProductService: MenuProductService, private dialog: MatDialog, private portionsService: PortionsService) {
    this.productForm = this.fb.group({
      categories: ['', Validators.required],
      sku: [''],
      nombre: ['', Validators.required],
      descripcion: ['', Validators.required],
      costo: ['', [Validators.required, Validators.min(0)]],
      venta: ['', [Validators.required, Validators.min(0)]],
      unitTypes: ['', Validators.required]
    });

    this.productForm.get('sku')?.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged()) // Evita peticiones innecesarias
      .subscribe(value => this.checkField('sku', value));

    this.productForm.get('nombre')?.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged()) // Evita peticiones innecesarias
      .subscribe(value => this.checkField('name', value));
  }

  checkField(field: string, value: string) {
    if (!value) {
      // Reset existence flags when field is cleared
      if (field === 'sku') this.skuExists = false;
      if (field === 'name') this.nombreExists = false;
      return; // No buscar si el input está vacío
    }

    this.menuProductService.validateIfExist(field, value)
      .subscribe(response => {
        console.log({ response, field, value })
        if (field === 'sku') {
          this.skuExists = response.exists;
        } else if (field === 'name') {
          this.nombreExists = response.exists;
        }
      });
  }

  ngOnInit() {
    // Prefill form if editing
    if (this.productInfo?.uuid) {
      this.loadPriceHistory();
    }

    if (this.productInfo) {
      const initialCategory = (
        this.productInfo.categoryUuid ||
        this.productInfo.categoryId ||
        this.productInfo.categoryHash ||
        this.productInfo.category?.uuid ||
        this.productInfo.category?.id ||
        ''
      );
      const initialUnitType = (
        this.productInfo.unitTypeUuid ||
        this.productInfo.unitTypeId ||
        this.productInfo.unitType?.uuid ||
        this.productInfo.unitType?.id ||
        ''
      );
      this.productForm.patchValue({
        categories: initialCategory,
        sku: this.productInfo.sku || '',
        nombre: this.productInfo.name || '',
        descripcion: this.productInfo.description || '',
        costo: this.productInfo.costPrice ?? '',
        venta: this.productInfo.sellPrice ?? '',
        unitTypes: initialUnitType
      });

      // Initialize options if editing
      if (this.productInfo?.options && Array.isArray(this.productInfo.options)) {
        console.log('AddMenuProductFrom - initializing options:', this.productInfo.options);
        this.options = this.productInfo.options.map((opt: any) => ({
          uuid: opt.uuid,
          name: opt.name || '',
          priceDelta: opt.priceDelta || 0,
          enabled: opt.enabled !== false,
          sortOrder: opt.sortOrder ?? 0
        }));
        console.log('AddMenuProductFrom - options after mapping:', this.options);
      } else {
        console.log('AddMenuProductFrom - no options found, initializing empty array');
        this.options = [];
      }
    }

    this.categoryService.getCategories().subscribe({
      next: (data: any) => {
      this.categories = [
        {
          name: 'Choose...',
        },
        ...data.map(({ name, uuid, categoryUnitTypes: unitTypes, portions, categoryPortions }: { name: string, uuid: string, categoryUnitTypes?: UnitType[], portions?: any[], categoryPortions?: any[] }) => {
          // Normalize possible shapes coming from API
          const normalizedUnitTypes = unitTypes || portions || categoryPortions || [];
          return { name, uuid, unitTypes: normalizedUnitTypes };
        })
      ];
      this.categoriesOptions = [...data.map(({ name, uuid }: { name: string, uuid: string }) => ({
        value: uuid,
        label: name
      }))];

      // If editing, pre-select category and portion
      const initialCat = (
        this.productInfo?.categoryUuid ||
        this.productInfo?.categoryId ||
        this.productInfo?.categoryHash ||
        this.productInfo?.category?.uuid ||
        this.productInfo?.category?.id ||
        ''
      );
      if (initialCat) {
        const normalize = (s: any) => (s ?? '').toString().normalize('NFD').replace(/\p{Diacritic}/gu,'').toLowerCase().trim();
        let found = this.categoriesOptions.find(({ value }) => value === initialCat);
        // Fallback: match by category name if id/uuid didn't match
        if (!found && (this.productInfo?.categoryName || this.productInfo?.category?.name)) {
          const name = normalize(this.productInfo?.categoryName || this.productInfo?.category?.name || '');
          found = this.categoriesOptions.find(({ label }) => normalize(label) === name);
        }
        this.selectedCategory = found?.value;
        // Defer setting the form control until after options render
        if (this.selectedCategory) {
          setTimeout(() => {
            this.productForm.get('categories')?.setValue(this.selectedCategory);
            this.cdr.detectChanges();
          }, 0);
        }
        if (this.selectedCategory) {
          this.updateUnitTypesByCategory(this.selectedCategory);
          const initialUnit = (
            this.productInfo?.unitTypeUuid ||
            this.productInfo?.unitTypeId ||
            this.productInfo?.unitType?.uuid ||
            this.productInfo?.unitType?.id ||
            ''
          );
          if (initialUnit) {
            // Defer setting to next tick to ensure options are ready
            setTimeout(() => {
              const exists = this.unitTypesOptions.some(o => o.value === initialUnit);
              if (exists) {
                this.selectedUnitType = initialUnit;
                this.productForm.get('unitTypes')?.setValue(initialUnit);
              } else if (this.unitTypesOptions.length > 0) {
                // Fallback: select first available portion so UI shows a value
                const first = this.unitTypesOptions[0]?.value;
                this.selectedUnitType = first;
                this.productForm.get('unitTypes')?.setValue(first);
              }
              this.cdr.detectChanges();
            }, 0);
          } else {
            // No initial unit provided: pick first available after options load
            setTimeout(() => {
              if ((this.unitTypesOptions?.length || 0) > 0) {
                const first = this.unitTypesOptions[0]?.value;
                this.selectedUnitType = first;
                this.productForm.get('unitTypes')?.setValue(first);
                this.cdr.detectChanges();
              }
            }, 0);
          }
        }
      }
      this.loading = false;
    },
    error: (e) => {
      console.error('Error loading categories in AddMenuProductFromComponent', e);
      this.loading = false;
    }});

  }

  checkIfFormIsValid() {
    return this.productForm?.valid && !this.skuExists && !this.nombreExists
  }

  async onSubmit(): Promise<void> {
    if (this.checkIfFormIsValid()) {
      console.log('Form Submitted!', this.productForm.value);
      const rawSku = (this.productForm.get('sku')?.value || '').toString().trim();
      const menuProduct: any = {
        name: (this.productForm.get('nombre')?.value || '').toString().trim(),
        description: (this.productForm.get('descripcion')?.value || '').toString().trim(),
        categoryUuid: this.productForm.get('categories')?.value,
        unitTypeUuid: this.productForm.get('unitTypes')?.value,
        costPrice: this.productForm.get('costo')?.value,
        sellPrice: this.productForm.get('venta')?.value,
        options: this.options.filter(opt => opt.name.trim()).map(opt => ({
          name: opt.name.trim(),
          priceDelta: Number(opt.priceDelta) || 0,
          enabled: opt.enabled !== false,
          sortOrder: opt.sortOrder ?? 0
        }))
      };
      if (rawSku) {
        menuProduct.sku = rawSku;
      }
      await this.menuProductService.createMenuProduct(menuProduct).toPromise();
      this.created.emit(menuProduct);
    } else {
      console.log('Form not valid');
      // Optionally, mark all fields as touched to trigger validation messages
      this.productForm?.markAllAsTouched();
    }
  }

  loadPriceHistory(): void {
    const uuid = this.productInfo?.uuid;
    if (!uuid) return;
    this.histLoading = true;
    this.menuProductService.getPriceHistory(uuid).subscribe({
      next: (data) => { this.priceHistory = Array.isArray(data) ? data : []; this.histLoading = false; },
      error: () => { this.histLoading = false; }
    });
  }

  savePriceHistory(): void {
    const uuid = this.productInfo?.uuid;
    if (!uuid || this.histSellPrice === null || this.histCostPrice === null || !this.histDate) return;
    this.histSaving = true;
    this.menuProductService.addPriceHistory(uuid, {
      sellPrice: this.histSellPrice,
      costPrice: this.histCostPrice,
      date: this.histDate
    }).subscribe({
      next: () => { this.histSaving = false; this.loadPriceHistory(); },
      error: () => { this.histSaving = false; }
    });
  }

  onCancel(): void {
    this.canceled.emit();
  }

  addOption(): void {
    this.options.push({
      name: '',
      priceDelta: 0,
      enabled: true,
      sortOrder: this.options.length
    });
  }

  removeOption(index: number): void {
    if (index >= 0 && index < this.options.length) {
      this.options.splice(index, 1);
    }
  }

  async onNewCategory() {
    const ref = this.dialog.open(CategoryDialogComponent, { data: { title: 'Nueva categoría', actionLabel: 'Guardar' } });
    const payload = await ref.afterClosed().toPromise();
    if (!payload) return;
    try {
      const created = await this.categoryService.createCategory(payload).toPromise();
      await this.reloadCategoriesOptions(created?.uuid);
    } catch (e) {
      console.error('Error creating category from product form', e);
    }
  }

  private async reloadCategoriesOptions(selectUuid?: string) {
    try {
      const data: any = await this.categoryService.getCategories().toPromise();
      this.categories = [
        { name: 'Choose...' },
        ...data.map(({ name, uuid, categoryUnitTypes: unitTypes, portions, categoryPortions }: { name: string, uuid: string, categoryUnitTypes?: UnitType[], portions?: any[], categoryPortions?: any[] }) => {
          const normalizedUnitTypes = unitTypes || portions || categoryPortions || [];
          return { name, uuid, unitTypes: normalizedUnitTypes } as any;
        })
      ];
      this.categoriesOptions = [...data.map(({ name, uuid }: { name: string, uuid: string }) => ({ value: uuid, label: name }))];
      if (selectUuid) {
        this.selectedCategory = selectUuid;
        this.productForm.get('categories')?.setValue(selectUuid);
        this.updateUnitTypesByCategory(selectUuid);
        this.cdr.detectChanges();
      }
    } catch (e) {
      console.error('Error reloading categories after creation', e);
    }
  }

  async onNewPortion() {
    const catList = (this.categories || []).filter((c: any) => !!c.uuid).map((c: any) => ({ uuid: c.uuid, name: c.name }));
    const ref = this.dialog.open(PortionDialogComponent, { data: { categories: catList } });
    const payload = await ref.afterClosed().toPromise();
    if (!payload) return;
    try {
      const created = await this.portionsService.createPortion(payload).toPromise();
      const currentCat = this.productForm.get('categories')?.value || this.selectedCategory;
      await this.reloadCategoriesOptions(currentCat);
      if (created?.uuid && payload.categoryUuids?.includes(currentCat)) {
        this.updateUnitTypesByCategory(currentCat);
        const exists = this.unitTypesOptions.some(o => o.value === created.uuid);
        if (exists) {
          this.selectedUnitType = created.uuid;
          this.productForm.get('unitTypes')?.setValue(created.uuid);
          this.cdr.detectChanges();
        }
      }
    } catch (e) {
      console.error('Error creating portion from product form', e);
    }
  }

  updateUnitTypesByCategory(uuid: string) {
    console.log('updateUnitTypesByCategory:', uuid, this.categories)
    const cat: any = this.categories.find((category: any) => category.uuid === uuid);
    // Accept multiple shapes: unitTypes, categoryUnitTypes, portions, categoryPortions
    let unitTypes: any[] = (cat?.unitTypes || cat?.categoryUnitTypes || cat?.portions || cat?.categoryPortions || []);

    // Si la categoría no tiene porciones propias (caso típico al crear una nueva),
    // usar como opciones todas las porciones existentes en otras categorías.
    if (!unitTypes || unitTypes.length === 0) {
      const seen = new Set<string>();
      const allUnitTypes: any[] = [];
      (this.categories || []).forEach((c: any) => {
        if (!c?.uuid || c.uuid === uuid) {
          return;
        }
        const list: any[] = (c.unitTypes || c.categoryUnitTypes || c.portions || c.categoryPortions || []);
        (list || []).forEach((u: any) => {
          const id = (u?.uuid || u?.id || '').toString();
          if (!id || seen.has(id)) {
            return;
          }
          seen.add(id);
          allUnitTypes.push(u);
        });
      });
      unitTypes = allUnitTypes;
    }

    this.unitTypesOptions = (unitTypes || []).map((u: any) => ({ label: u?.name, value: u?.uuid || u?.id }));
    this.cdr.detectChanges();
    // Preserve selected unit type if it's valid (editing scenario)
    const current = this.productForm.get('unitTypes')?.value || this.productInfo?.unitTypeUuid || this.selectedUnitType || '';
    const exists = this.unitTypesOptions.some(o => o.value === current);
    if (exists) {
      this.selectedUnitType = current;
      this.productForm.get('unitTypes')?.setValue(current);
    } else {
      this.selectedUnitType = '';
      // If empty, and options exist, default to first option
      if ((this.unitTypesOptions?.length || 0) > 0) {
        const first = this.unitTypesOptions[0]?.value;
        this.productForm.get('unitTypes')?.setValue(first);
        this.selectedUnitType = first;
      } else {
        this.productForm.get('unitTypes')?.setValue('');
      }
    }
    console.log('updateUnitTypesByCategory -> options:', this.unitTypesOptions)
  }

  onCategoryChange(value: any) {
    console.log('onCategoryChange:', value)
    this.updateUnitTypesByCategory(value.value);
  }

  onUnitTypeChange(value: any) {
    console.log('onUnitTypeChange:', value)
    // this.productForm.get('unitTypes')?.setValue(value.data);
  }

  // allCategories = [
  //   {
  //     name: 'Choose...',
  //   },
  //   {
  //     name: 'Option 1',
  //   },
  //   {
  //     name: 'Option 2',
  //   },
  //   {
  //     name: 'Option 3',
  //   }
  // ];

  // allUnitTypes = [
  //   {
  //     name: 'UnitTypes...',
  //   },
  //   {
  //     name: 'Unidad',
  //     id: 1
  //   },
  //   {
  //     name: 'Media Docena',
  //     id: 2
  //   },
  //   {
  //     name: 'Docena',
  //     id: 3
  //   }
  // ]

}